import React from "react";
import {
  Container,
  Typography,
  Box,
  Divider,
  Paper,
} from "@mui/material";
import { Helmet } from "react-helmet";
import Header from "../common/Header";

const PrivacyPolicy: React.FC = () => {
  return (
    <div>
      <Helmet>
        <title>Privacy Policy | Tranzcribe</title>
        <meta
          name="description"
          content="Privacy Policy for Tranzcribe's AI-powered transcription service."
        />
      </Helmet>

      {/* Navigation Bar - Sticky */}
      <Header />

      {/* Content */}
      <Container maxWidth="md" sx={{ mt: 6, mb: 4, paddingBottom: 4 }}>
        <Paper elevation={3} sx={{ p: 4 }}>
          <Typography variant="h4" gutterBottom>
            Privacy Policy
          </Typography>
          <Typography color="textSecondary" sx={{ mb: 4 }}>
            Last updated: 6/3/2025
          </Typography>
          <Divider sx={{ mb: 4 }} />

          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" gutterBottom>
              1. Introduction
            </Typography>
            <Typography variant="body1" paragraph>
              At Tranzcribe, we take your privacy seriously. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our transcription service.
            </Typography>
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" gutterBottom>
              2. Information We Collect
            </Typography>
            <Typography variant="h6" gutterBottom>
              2.1 Personal Information
            </Typography>
            <Typography variant="body1" paragraph>
              We may collect:
            </Typography>
            <ul>
              <li>
                <Typography variant="body1">Name and email address</Typography>
              </li>
              <li>
                <Typography variant="body1">Billing information</Typography>
              </li>
              <li>
                <Typography variant="body1">Company information (for business accounts)</Typography>
              </li>
              <li>
                <Typography variant="body1">Usage data and preferences</Typography>
              </li>
            </ul>

            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
              2.2 Audio and Transcription Data
            </Typography>
            <Typography variant="body1" paragraph>
              We collect and process the audio files you upload and the resulting transcriptions. This data is handled in accordance with our security protocols and data retention policies.
            </Typography>
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" gutterBottom>
              3. How We Use Your Information
            </Typography>
            <Typography variant="body1" paragraph>
              We use your information to:
            </Typography>
            <ul>
              <li>
                <Typography variant="body1">Provide and maintain our service</Typography>
              </li>
              <li>
                <Typography variant="body1">Process your payments</Typography>
              </li>
              <li>
                <Typography variant="body1">Send you service updates and notifications</Typography>
              </li>
              <li>
                <Typography variant="body1">Improve our transcription accuracy</Typography>
              </li>
              <li>
                <Typography variant="body1">Respond to your inquiries and support requests</Typography>
              </li>
              <li>
                <Typography variant="body1">Detect and prevent fraudulent activities</Typography>
              </li>
            </ul>
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" gutterBottom>
              4. Data Security
            </Typography>
            <Typography variant="body1" paragraph>
              We implement appropriate technical and organizational measures to protect your personal information, including encryption of data in transit and at rest. However, no method of transmission over the Internet is 100% secure.
            </Typography>
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" gutterBottom>
              5. Data Retention
            </Typography>
            <Typography variant="body1" paragraph>
              We retain your personal information and transcription data for as long as necessary to provide our services and comply with legal obligations. You can request deletion of your data at any time.
            </Typography>
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" gutterBottom>
              6. Your Rights
            </Typography>
            <Typography variant="body1" paragraph>
              You have the right to:
            </Typography>
            <ul>
              <li>
                <Typography variant="body1">Access your personal information</Typography>
              </li>
              <li>
                <Typography variant="body1">Correct inaccurate data</Typography>
              </li>
              <li>
                <Typography variant="body1">Request deletion of your data</Typography>
              </li>
              <li>
                <Typography variant="body1">Object to processing of your data</Typography>
              </li>
              <li>
                <Typography variant="body1">Export your data in a portable format</Typography>
              </li>
            </ul>
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" gutterBottom>
              7. Cookies and Tracking
            </Typography>
            <Typography variant="body1" paragraph>
              We use cookies and similar tracking technologies to improve your experience on our website. You can control cookie preferences through your browser settings.
            </Typography>
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" gutterBottom>
              8. Changes to This Policy
            </Typography>
            <Typography variant="body1" paragraph>
              We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new policy on this page and updating the "Last updated" date.
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" gutterBottom>
              9. Contact Us
            </Typography>
            <Typography variant="body1">
              If you have any questions about this Privacy Policy, please contact us at privacy@tranzcribe.com.
            </Typography>
          </Box>
        </Paper>
      </Container>

      {/* Footer */}
      <Box 
        component="footer" 
        sx={{ 
          py: 6, 
          textAlign: "center",
          bgcolor: "#f5f5f7"
        }}
      >
        <Container maxWidth="lg">
          <Divider sx={{ mb: 4 }} />
          <Typography variant="body2" color="text.secondary" mb={2}>
            © {new Date().getFullYear()} Tranzcribe. All rights reserved.
          </Typography>
        </Container>
      </Box>
    </div>
  );
};

export default PrivacyPolicy; 